/**
 * Admin sign-in. Document-grade split layout: brand panel + form. Inline validation,
 * loading state, error surfacing, dev-credentials hint.
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Eye, EyeOff, ShieldCheck, Mail } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider.jsx';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import styles from './Login.module.css';

export default function Login() {
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await login(email.trim(), password);
      toast.success(`Welcome back, ${user.name.split(' ')[0]}.`);
      navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <aside className={styles.brandPanel}>
        <div className={styles.brandInner}>
          <div className={styles.mark} aria-hidden="true">
            <span className={styles.markRing} />
            <span className={styles.markDot} />
          </div>
          <h1 className={styles.brandTitle}>
            USMEHAR <span className={styles.accent}>CARE</span>
          </h1>
          <p className={styles.brandTag}>Impact &amp; Transparency Platform — staff console.</p>
          <ul className={styles.points}>
            <li>
              <ShieldCheck size={18} /> Tiered approval &amp; immutable audit trail
            </li>
            <li>
              <ShieldCheck size={18} /> Campaign-safe master switch
            </li>
            <li>
              <ShieldCheck size={18} /> Every rupee accounted for
            </li>
          </ul>
        </div>
      </aside>

      <main className={styles.formPanel}>
        <form className={styles.form} onSubmit={submit}>
          <p className="eyebrow">Sign in</p>
          <h2 className={styles.formTitle}>Staff console</h2>
          <p className={styles.formLede}>Use your registered email and password.</p>

          {error && <div className={styles.error}>{error}</div>}

          <label className={styles.label}>
            Email
            <div className={styles.pwWrap}>
              <input
                className={styles.input}
                type="email"
                inputMode="email"
                autoComplete="username"
                placeholder="you@usmeharcare.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <span className={styles.pwToggle} aria-hidden="true">
                <Mail size={18} />
              </span>
            </div>
          </label>

          <label className={styles.label}>
            Password
            <div className={styles.pwWrap}>
              <input
                className={styles.input}
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className={styles.pwToggle}
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>

          <button className={styles.submit} type="submit" disabled={busy}>
            {busy ? 'Signing in…' : (<><LogIn size={18} /> Sign in</>)}
          </button>

          <p className={styles.devHint}>
            Dev: <code>admin@usmeharcare.org</code> / <code>usmehar123</code> (super admin)
          </p>
        </form>
      </main>
    </div>
  );
}
